import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ROLE_LABELS, type Role } from '@saesak/shared';
import { api, ApiError } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { AuthLayout } from './AuthLayout';

interface InvitePreview {
  facilityName: string;
  role: Role;
  childName: string | null;
  expiresAt: string;
}

/** 초대코드 확인 — 어떤 기관·아이와 연결되는지 먼저 보여 준다 (§4) */
export function InvitePage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function check(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api<InvitePreview>(`/auth/invites/${code.trim().toUpperCase()}`);
      setPreview(data);
    } catch (caught) {
      setPreview(null);
      setError(
        caught instanceof ApiError ? caught.message : '연결이 잠시 불안정해요. 다시 시도해 주세요.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="초대코드로 시작하기"
      description="원에서 받으신 코드를 넣어 주세요."
      footer={
        <Link to="/login" className="underline">
          로그인 화면으로
        </Link>
      }
    >
      <form onSubmit={check} className="flex flex-col gap-4">
        <Field label="초대코드" required>
          <Input
            required
            autoCapitalize="characters"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABCD1234"
            className="tracking-[0.2em]"
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        {preview ? (
          <div className="rounded-sm bg-primary-soft p-4">
            <p className="text-base font-semibold text-ink">{preview.facilityName}</p>
            <p className="mt-1 text-sm text-ink-sub">
              {ROLE_LABELS[preview.role]}
              {preview.childName ? ` · ${preview.childName}` : ''}(으)로 연결돼요.
            </p>
            <Button
              block
              size="lg"
              className="mt-4"
              onClick={() => navigate(`/register?code=${encodeURIComponent(code.trim().toUpperCase())}`)}
            >
              이어서 가입하기
            </Button>
          </div>
        ) : (
          <Button type="submit" block size="lg" loading={loading}>
            코드 확인
          </Button>
        )}
      </form>
    </AuthLayout>
  );
}
